import {
  AppBar,
  Box,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  Tooltip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import { styled } from '@mui/material/styles';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useUserInterface } from '../../contexts/UserInterfaceContext';
import { SelectionContext } from '../../contexts/SelectionContext';
import FlashyButton from '../common/FlashyButton';
import { useSpinner } from '../../contexts/SpinnerContext';
import { ApiEndpoint } from '../../types';
import UserButton from '../login/UserButton';
import { CMChip } from '../common/CMChip';


const Logo = styled("img")({
  marginRight: 10,
  height: "50px",
});

interface TitleBarProps {
  currentTab: number;
  setCurrentTab: (value: number) => void;
  setCurrentEndpoint: Dispatch<SetStateAction<ApiEndpoint | null>>;
  onOpenAdminModal: () => void;
  currentEndpoint?: ApiEndpoint | null; // Add current endpoint prop for timeline toggle
}

/**
 * Calculate admin tab index dynamically based on whether dashboards tab is enabled
 * @param hasDashboardsTab - Whether the dashboards/tools tab is enabled
 * @returns The index position of the Admin tab
 */
const getAdminTabIndex = (hasDashboardsTab: boolean) => hasDashboardsTab ? 6 : 5;

const TitleBar: React.FC<TitleBarProps> = ({
  currentTab,
  setCurrentTab,
  setCurrentEndpoint,
  onOpenAdminModal,
  currentEndpoint,
}) => {
  const config = useConfig();
  const { filterArea, selectedGeoEntity, setSelectedEntity, setFilterArea } = useContext(SelectionContext);
  const { loading } = useSpinner();
  const { 
    openDataDrawerFullscreen, 
    closeDataDrawer, 
    setMediaDrawerVisible, 
    triggerClosePopups,
    getCurrentTimelineVisibility,
    toggleTimelineForEndpoint 
  } = useUserInterface();
  
  // Check if dashboards tab is enabled (default true for backward compatibility)
  const hasDashboardsTab = config.dashboardsTab?.enabled !== false;
  const dashboardsLabel = config.dashboardsTab?.label || "Tools";
  const adminTabIndex = getAdminTabIndex(hasDashboardsTab);
  
  // Get timeline visibility for current endpoint (elegant!)
  const isTimelineVisible = getCurrentTimelineVisibility(currentEndpoint || null);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    if (newValue === adminTabIndex) {
      onOpenAdminModal();
    } else {
      setCurrentTab(newValue);
      
      // Handle DataDrawer for Tables tab (tab 2)
      if (newValue === 2) {
        openDataDrawerFullscreen(); // Open DataDrawer in fullscreen mode for Tables tab
        setMediaDrawerVisible(false); // Hide MediaDrawer when in Tables view
        setSelectedEntity(null); // Clear selection to default to DataGrid view for browsing
      } else {
        // Close DataDrawer for other tabs
        closeDataDrawer();
        // MediaDrawer visibility will be controlled by its own logic based on selected entity
      }
    }
  };

  const handleClearSelection = () => {
    // Clear selections
    setSelectedEntity(null);
    setFilterArea(null, false); // Don't show filtered layers when clearing
    
    // Close any open popups on the map
    triggerClosePopups();
  };

  return (
    <AppBar position="static" sx={{ zIndex: 1300 }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2, marginLeft: 5 }}
        >
          <Logo src={config.logoPath} alt="Icon" />
        </IconButton>

        <Typography variant="h1" component="div" sx={{ fontWeight: "bold" }}>
          {config.appTitle}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {loading && (
          <Box sx={{ display: "flex" }}>
            <CircularProgress color="secondary" />
          </Box>
        )}

        {/* Timeline Available Indicator - FlashyButton for SHOW, regular for HIDE */}
        {(currentTab === 1 || currentTab === 2) && currentEndpoint?.dateConfig?.hasDateFilter && (
          !isTimelineVisible ? (
            // SHOW TIMELINE - Use FlashyButton with pink-purple flash
            <FlashyButton
              variant="outlined"
              size="small"
              onClick={() => currentEndpoint?.name && toggleTimelineForEndpoint(currentEndpoint.name)}
              flashColors={['#ff69b4', '#9966cc']} // Pink to purple
              borderColor="rgba(255, 255, 255, 0.4)"
              textColor="white"
              pulseInterval={2}
              flashInterval={2}
              flashOpacity={0.5}
              flashWidth="80%"
              sx={{
                mx: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              }}
            >
              <TimelineIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
              SHOW TIMELINE
            </FlashyButton>
          ) : (
            // HIDE TIMELINE - Use regular Box (no flash needed)
            <Box
              onClick={() => currentEndpoint?.name && toggleTimelineForEndpoint(currentEndpoint.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mx: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              <TimelineIcon 
                sx={{
                  color: 'secondary.main',
                  fontSize: '1.1rem'
                }}
              />
              <Typography 
                variant="caption" 
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              >
                HIDE TIMELINE
              </Typography>
            </Box>
          )
        )}
        
        {(selectedGeoEntity || filterArea) && (
          <CMChip
            label={selectedGeoEntity?.name || filterArea || ''}
            onDelete={handleClearSelection}
            sx={{ 
              mx: 2,
              backgroundColor: 'rgba(26, 26, 26, 0.9)',
              backdropFilter: 'blur(8px)',
              color: 'white',
              height: '40px',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                backgroundColor: 'rgba(40, 40, 40, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                transform: 'translateY(-1px)'
              },
              '& .MuiChip-label': {
                paddingLeft: '20px',
                paddingRight: '12px',
                py: 1,
                fontSize: '1.1rem',
                fontWeight: 600,
                fontFamily: 'inherit',
                letterSpacing: '0.02em'
              },
              '& .MuiChip-deleteIcon': {
                color: 'rgba(255, 255, 255, 0.8)',
                marginLeft: '16px',
                fontSize: '1.3rem',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  transform: 'scale(1.1)'
                }
              }
            }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
        >
          <Tab label="Home" />
          <Tab label="Map" />
          <Tab label="Tables" />
          <Tab label="Upload" />
          <Tab label="Editor" />
          {hasDashboardsTab && (
            <Tab label={dashboardsLabel} />
          )}
          <Tab label="Admin" />
        </Tabs>
        
        {/* Enhanced Timeline Toggle - Show on Map and Tables tabs */}
        {(currentTab === 1 || currentTab === 2) && (
          <Tooltip title={isTimelineVisible ? "Hide Timeline" : "Show Timeline"}>
            <IconButton
              onClick={() => currentEndpoint?.name && toggleTimelineForEndpoint(currentEndpoint.name)}
              disabled={!currentEndpoint?.dateConfig?.hasDateFilter}
              sx={{
                color: isTimelineVisible ? 'secondary.main' : 'rgba(255, 255, 255, 0.9)',
                mx: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: isTimelineVisible 
                  ? 'rgba(255, 255, 255, 0.15)' 
                  : 'rgba(255, 255, 255, 0.05)',
                border: `2px solid ${
                  currentEndpoint?.dateConfig?.hasDateFilter
                    ? isTimelineVisible 
                      ? 'rgba(255, 255, 255, 0.3)' 
                      : 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(255, 255, 255, 0.1)'
                }`,
                transition: 'all 0.3s ease-in-out',
                // Pulsing animation for date-enabled endpoints when timeline is hidden
                animation: currentEndpoint?.dateConfig?.hasDateFilter && !isTimelineVisible 
                  ? 'pulse 2.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.4)'
                  },
                  '70%': {
                    boxShadow: '0 0 0 6px rgba(255, 255, 255, 0)'
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)'
                  }
                },
                '&:hover': {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  transform: 'translateY(-2px)',
                  animation: 'none' // Stop pulsing on hover
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  animation: 'none'
                }
              }}
            >
              <TimelineIcon />
            </IconButton>
          </Tooltip>
        )}
        
        <UserButton />
      </Toolbar>
    </AppBar>
  );
};

export default TitleBar;
